"use strict";
/* eslint-disable @typescript-eslint/no-explicit-any */
Object.defineProperty(exports, "__esModule", { value: true });
exports.splitPathIntoParts = exports.config = exports.moveDataValue = exports.removeDataValue = exports.setDataValue = exports.getDataValue = void 0;
/**
 * Gets the value from the data object based on a given path.
 * Returns undefined if there is no value for a given path.
 *
 * @param data - data object
 * @param path - path to the value
 */
function getDataValue(data, path) {
    const pathList = splitPathIntoParts(path);
    if (pathList.length === 0) {
        return data;
    }
    for (const key of path.split('.')) {
        if (key.startsWith('!')) {
            continue;
        }
        if (arrayIndexPartRegex.test(key)) {
            if (key.endsWith('+')) {
                return undefined;
            }
            const index = parseInt(key.substring(1));
            if (Array.isArray(data) && isFinite(index) && index >= 0 && index < data.length) {
                data = data[index];
            }
            else {
                return undefined;
            }
        }
        else if (objectFieldPartRegex.test(key)) {
            if (isObject(data)) {
                data = data[key];
            }
            else {
                return undefined;
            }
        }
        else {
            return undefined;
        }
        if (data == null) {
            return undefined;
        }
    }
    return data;
}
exports.getDataValue = getDataValue;
/**
 * Mutates the data object by setting the value for a given path.
 * Parent objects and/or arrays are created as necessary.
 * Return true if the value was changed, false otherwise.
 *
 * @param data - object to mutate
 * @param path - path to the value that should be set, if path ends with '+' then value is inserted into array at the specified index, otherwise value is set at the specified index
 * @param value - value to set
 */
function setDataValue(data, path, value) {
    const pathList = splitPathIntoParts(path);
    if (pathList.length === 0) {
        return false;
    }
    let insert = false;
    let changed = false;
    for (let i = 0; i < pathList.length; i++) {
        const key = pathList[i];
        let index;
        if (arrayIndexPartRegex.test(key)) {
            if (key.endsWith('+')) {
                if (i === pathList.length - 1) {
                    insert = true;
                }
                else {
                    return false;
                }
            }
            index = parseInt(key.substring(1));
            if (!Array.isArray(data) || !isFinite(index) || index >= exports.config.maxArrayLength) {
                // this can only happen when first part of path is an array index, but the data is not an array
                return false;
            }
        }
        else if (objectFieldPartRegex.test(key)) {
            if (typeof data !== 'object' || Array.isArray(data)) {
                // this can only happen when first part of path is a field, but the data is an array
                return false;
            }
            index = key;
        }
        else {
            return false;
        }
        if (i + 1 < pathList.length) {
            // look ahead
            const nextKey = pathList[i + 1];
            if (arrayIndexPartRegex.test(nextKey)) {
                // next part should be an array
                if (nextKey.endsWith('+')) {
                    if (i + 1 !== pathList.length - 1) {
                        return false;
                    }
                }
                const nextIndex = parseInt(nextKey.substring(1));
                if (!isFinite(nextIndex) || nextIndex >= exports.config.maxArrayLength) {
                    return false;
                }
                if (!Array.isArray(data[index])) {
                    // it's not, so create an array
                    data[index] = [];
                }
                data = data[index];
                if (data.length < nextIndex) {
                    data.length = nextIndex;
                }
            }
            else if (objectFieldPartRegex.test(nextKey)) {
                // next part should be an object
                if (!isObject(data[index])) {
                    // it's not, so create an object
                    data[index] = {};
                }
                data = data[index];
            }
            else {
                return false;
            }
        }
        else {
            // last part
            if (insert && typeof index === 'number' && Array.isArray(data)) {
                changed = true;
                if (index > data.length) {
                    data.length = index;
                    data.push(value);
                }
                else if (index === data.length) {
                    data.push(value);
                }
                else {
                    data.splice(index, 0, value);
                }
            }
            else {
                changed = data[index] !== value;
                data[index] = value;
            }
        }
    }
    return changed;
}
exports.setDataValue = setDataValue;
/**
 * Mutates the data object by removing the value for a given path.
 * Returns the removed value or undefined if no value exists for a given path.
 *
 * @param data - object to mutate
 * @param path - path to value that should be removed
 */
function removeDataValue(data, path) {
    const pathList = splitPathIntoParts(path);
    if (pathList.length === 0) {
        return undefined;
    }
    const parentPath = pathList.slice(0, -1).join('.');
    const parentValue = getDataValue(data, parentPath);
    if (parentValue != null) {
        const lastKey = pathList[pathList.length - 1];
        if (arrayIndexPartRegex.test(lastKey)) {
            if (lastKey.endsWith('+')) {
                return undefined;
            }
            const index = parseInt(lastKey.substring(1));
            if (Array.isArray(parentValue) && index < parentValue.length) {
                return parentValue.splice(index, 1)[0];
            }
            else {
                return undefined;
            }
        }
        else if (objectFieldPartRegex.test(lastKey)) {
            if (isObject(parentValue)) {
                const deletedValue = parentValue[lastKey];
                delete parentValue[lastKey];
                return deletedValue;
            }
            else {
                return undefined;
            }
        }
        else {
            return undefined;
        }
    }
    else {
        return undefined;
    }
}
exports.removeDataValue = removeDataValue;
/**
 * Mutates the data object by moving a value from one path to the other.
 * Returns the moved value or undefined if no value exists for a given path.
 *
 * @param data - object to mutate
 * @param pathFrom - path to the source value
 * @param pathTo - path to the destination, if path ends with '+' then value is inserted into array at the specified index, otherwise value is set at the specified index
 */
function moveDataValue(data, pathFrom, pathTo) {
    const pathListFrom = splitPathIntoParts(pathFrom);
    if (pathListFrom.length === 0) {
        return undefined;
    }
    pathFrom = pathListFrom.join('.');
    const pathListTo = splitPathIntoParts(pathTo);
    if (pathListTo.length === 0) {
        return undefined;
    }
    pathTo = pathListTo.join('.');
    if (pathTo === pathFrom) {
        return getDataValue(data, pathFrom);
    }
    else if (pathTo.startsWith(pathFrom + '.')) {
        // cannot move to itself
        return undefined;
    }
    const value = getDataValue(data, pathFrom);
    if (value != null) {
        if (setDataValue(data, pathTo, value)) {
            const pathListToIndexLast = pathListTo.length - 1;
            const lastKeyTo = pathListTo[pathListToIndexLast];
            if (arrayIndexPartRegex.test(lastKeyTo) && lastKeyTo.endsWith('+')) {
                const lastIndexTo = parseInt(lastKeyTo.substring(1));
                if (pathListFrom.slice(0, pathListToIndexLast).join('.') === pathListTo.slice(0, pathListToIndexLast).join('.')) {
                    const indexFromAtSameDepth = parseInt(pathListFrom[pathListToIndexLast].substring(1));
                    if (indexFromAtSameDepth >= lastIndexTo) {
                        pathListFrom[pathListToIndexLast] = '#' + (indexFromAtSameDepth + 1);
                        pathFrom = pathListFrom.join('.');
                    }
                }
            }
            removeDataValue(data, pathFrom);
            return value;
        }
        else {
            return undefined;
        }
    }
    else {
        return undefined;
    }
}
exports.moveDataValue = moveDataValue;
/**
 * Basic check for an object. Returns true if the value is an object.
 * Meant to be used only with JSON data types and undefined value.
 *
 * @param obj - value to test
 */
function isObject(obj) {
    return obj != null && typeof obj === 'object' && !Array.isArray(obj);
}
/**
 * Library configuration default values.
 */
exports.config = {
    /**
     * When mutating data this is the maximum array length.
     */
    maxArrayLength: 1000000,
};
/**
 * Pattern for array index path part, the '+' sign at the end is valid only for the last part of the path.
 */
const arrayIndexPartRegex = /^#\d+\+?$/;
/**
 * Pattern for field name path part.
 */
const objectFieldPartRegex = /^[\w-]+$/;
/**
 * Splits path into parts. Filters out ignored parts, these are the ones that start with '!'.
 *
 * @param path - path to split
 */
function splitPathIntoParts(path) {
    return path.split('.').filter((part) => part !== '' && !part.startsWith('!'));
}
exports.splitPathIntoParts = splitPathIntoParts;
